import { useMemo, useState } from 'react';

import { AlertCircle, ArrowRight, CalendarDays, Clock, Info, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

import {
  PRAZO_MAXIMO,
  ROTULO_DA_UNIDADE,
  UNIDADES_DE_PRAZO,
  type PrazoDoSistema,
  type UnidadeDePrazo,
} from './contrato';
import { mensagemDoErro, useAlterarPrazo, usePrazosDoSistema } from './dados';
import { agruparPorCategoria, calcularVencimento } from './vencimento';

/**
 * `/admin/parametros/prazos/sistema` — os prazos parametrizáveis do sistema,
 * agrupados por categoria, com um simulador de vencimento.
 *
 * DADO REAL, DA API (`bff/v1/admin/prazos-sistema`, migração 0321). Editar grava
 * de verdade, e a alteração vale para os cálculos feitos a partir dela. Duas
 * regras vêm do banco e a tela as mostra em vez de escondê-las:
 *   - prazo que uma regra do sistema lê não se desativa (o interruptor "Ativo"
 *     aparece travado, com o motivo);
 *   - quando a regra lê o valor cru e ignora a unidade, a unidade não muda (o
 *     seletor aparece travado).
 * Não há "Novo prazo" nem "Excluir": a tela ajusta os prazos que o sistema já usa.
 *
 * O SIMULADOR é aritmética de calendário, roda inteiro no navegador e se anuncia
 * como estimativa: o cálculo oficial é o do servidor.
 *
 * Vizinha de `parametros/configuracoes/slas` (SLAs institucionais), que trata os
 * quatro prazos fixos da apuração mensal em cartões próprios. A sobreposição está
 * registrada em `docs/ALVOS.md` ("duas telas parametrizam prazo pelos dois lados")
 * para ser resolvida quando a API de configuração existir.
 */
export function PrazosSistemaPage() {
  const { data, isLoading, isError, error, refetch } = usePrazosDoSistema();
  const [emEdicao, setEmEdicao] = useState<PrazoDoSistema | null>(null);

  const prazos = data?.itens;
  const grupos = useMemo(() => agruparPorCategoria(prazos), [prazos]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prazos do sistema"
        description="SLAs, alertas de vencimento e prazos operacionais. A alteração vale para os cálculos feitos a partir dela; prazos já em curso não são recontados."
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={mensagemDoErro(
            error,
            'Não foi possível carregar os prazos agora. Verifique sua conexão e tente novamente.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <Spinner />
      ) : grupos.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="Nenhum prazo parametrizado"
          description="Sem prazo cadastrado, o sistema usa os valores padrão embutidos em cada regra."
        />
      ) : (
        <>
          <SimuladorDeVencimento prazos={prazos ?? []} />

          <div className="grid gap-4">
            {grupos.map(([categoria, itens]) => (
              <Card key={categoria}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{categoria}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {itens.map((prazo) => (
                    <div
                      key={prazo.id}
                      className="flex flex-col justify-between gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{prazo.descricao}</span>
                          {!prazo.ativo && (
                            <Badge variant="outline" className="text-xs">
                              Inativo
                            </Badge>
                          )}
                        </div>
                        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                          {prazo.chave}
                        </p>
                        {prazo.usadoPor && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Usado pelo sistema: {prazo.usadoPor}
                          </p>
                        )}
                        {prazo.observacoes && (
                          <p className="mt-1 text-xs text-muted-foreground">{prazo.observacoes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-base font-semibold">{prazo.valor}</div>
                          <div className="text-xs text-muted-foreground">
                            {ROTULO_DA_UNIDADE[prazo.unidade]}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEmEdicao(prazo)}
                          aria-label={`Editar ${prazo.descricao}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {emEdicao && <EditorDePrazo prazo={emEdicao} onFechar={() => setEmEdicao(null)} />}
    </div>
  );
}

interface SimuladorDeVencimentoProps {
  prazos: PrazoDoSistema[];
}

/**
 * Calcula o vencimento de um prazo a partir de uma data escolhida.
 *
 * Não chama API: é aritmética de calendário. A contagem de dias úteis considera
 * só os feriados nacionais de data fixa — os móveis e os municipais dependem de
 * norma, e o cálculo que vale é o do banco. Por isso o aviso abaixo.
 */
function SimuladorDeVencimento({ prazos }: SimuladorDeVencimentoProps) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [dataBase, setDataBase] = useState(hoje);
  const [chaveEscolhida, setChaveEscolhida] = useState(prazos[0]?.chave ?? '');

  const prazo = prazos.find((item) => item.chave === chaveEscolhida) ?? prazos[0];

  const vencimento = useMemo(() => {
    if (!prazo || !dataBase) {
      return null;
    }
    // `new Date('2026-03-01')` seria lido em UTC e voltaria como fevereiro em
    // fuso negativo. Montando por partes, a data fica no fuso local.
    const [ano, mes, dia] = dataBase.split('-').map(Number);
    if (!ano || !mes || !dia) {
      return null;
    }
    return calcularVencimento(new Date(ano, mes - 1, dia), prazo.valor, prazo.unidade);
  }, [prazo, dataBase]);

  if (!prazo) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
          Simulador de vencimento
        </CardTitle>
        <CardDescription>
          Escolha um prazo e uma data base para ver quando ele venceria.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="simulador-prazo">Prazo</Label>
            <Select value={prazo.chave} onValueChange={setChaveEscolhida}>
              <SelectTrigger id="simulador-prazo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {prazos.map((item) => (
                  <SelectItem key={item.chave} value={item.chave}>
                    {item.descricao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="simulador-data">Data base</Label>
            <Input
              id="simulador-data"
              type="date"
              value={dataBase}
              onChange={(evento) => setDataBase(evento.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3 text-sm">
          <span>{formatarData(dataBase)}</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Badge variant="secondary">
            {prazo.valor} {ROTULO_DA_UNIDADE[prazo.unidade].toLowerCase()}
          </Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="font-semibold">
            {vencimento ? formatarData(vencimento.toISOString()) : '—'}
          </span>
        </div>

        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Estimativa</AlertTitle>
          <AlertDescription className="text-xs">
            A contagem de dias úteis aqui considera apenas os feriados nacionais de data fixa. Os
            feriados móveis e os municipais entram no cálculo oficial, feito pelo servidor — o
            vencimento real pode cair um ou dois dias depois.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

interface EditorDePrazoProps {
  prazo: PrazoDoSistema;
  onFechar: () => void;
}

function EditorDePrazo({ prazo, onFechar }: EditorDePrazoProps) {
  const alterar = useAlterarPrazo(prazo.id);
  const [valor, setValor] = useState(String(prazo.valor));
  const [unidade, setUnidade] = useState<UnidadeDePrazo>(prazo.unidade);
  const [ativo, setAtivo] = useState(prazo.ativo);
  const [observacoes, setObservacoes] = useState(prazo.observacoes ?? '');
  const [recusa, setRecusa] = useState<string | null>(null);

  const usadoPeloSistema = prazo.usadoPor !== null;
  const unidadeTravada = prazo.unidadeFixa !== null;

  function salvar() {
    setRecusa(null);
    const numero = Number(valor);
    if (valor.trim() === '' || !Number.isInteger(numero) || numero < 0 || numero > PRAZO_MAXIMO) {
      setRecusa(`O prazo precisa ser um número inteiro de 0 a ${PRAZO_MAXIMO}.`);
      return;
    }
    alterar.mutate(
      {
        versao: prazo.versao,
        valor: numero,
        unidade,
        ativo,
        observacoes: observacoes.trim() === '' ? null : observacoes,
      },
      {
        onSuccess: () => {
          toast.success('Prazo atualizado.');
          onFechar();
        },
        onError: (erro) =>
          setRecusa(mensagemDoErro(erro, 'Não foi possível salvar o prazo agora. Tente de novo.')),
      },
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title={prazo.descricao}
      description={prazo.chave}
      desktopMaxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onFechar} disabled={alterar.isPending}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={alterar.isPending}>
            {alterar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        {recusa && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{recusa}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="prazo-valor">Valor</Label>
            <Input
              id="prazo-valor"
              type="number"
              min={0}
              max={PRAZO_MAXIMO}
              step={1}
              value={valor}
              onChange={(evento) => setValor(evento.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="prazo-unidade">Unidade</Label>
            <Select
              value={unidade}
              disabled={unidadeTravada}
              onValueChange={(escolha) => setUnidade(escolha as UnidadeDePrazo)}
            >
              <SelectTrigger id="prazo-unidade">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNIDADES_DE_PRAZO.map((item) => (
                  <SelectItem key={item} value={item}>
                    {ROTULO_DA_UNIDADE[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {unidadeTravada && (
              <p className="text-xs text-muted-foreground">
                A regra que usa este prazo só funciona em{' '}
                {ROTULO_DA_UNIDADE[prazo.unidadeFixa!].toLowerCase()}.
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="prazo-observacoes">Observações</Label>
          <Textarea
            id="prazo-observacoes"
            rows={3}
            maxLength={1000}
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
            placeholder="Quando e como este prazo é aplicado."
          />
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
          <Label htmlFor="prazo-ativo" className="cursor-pointer">
            <span className="text-sm font-medium">Ativo</span>
            <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
              {usadoPeloSistema
                ? 'Uma regra do sistema usa este prazo, e ele não pode ser desativado.'
                : 'Inativo faz o sistema voltar ao valor padrão embutido na regra.'}
            </span>
          </Label>
          <Switch
            id="prazo-ativo"
            checked={ativo}
            disabled={usadoPeloSistema}
            onCheckedChange={setAtivo}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default PrazosSistemaPage;
